/**
 * Crossword layout: a word list in, a numbered free-form crossword out.
 *
 * The method is the standard one for generated puzzles. The longest answer goes in
 * first, across. Every later answer must cross a letter already on the board, and a
 * placement is legal only if it obeys the two rules that make a grid read as a
 * crossword: a new letter may not touch another word side-on (which would spell a word
 * nobody clued), and nothing may sit directly before or after a word. Among the legal
 * placements the search prefers the most crossings, then the smallest grid.
 *
 * The search is depth-first with backtracking: at each word it tries the best few
 * placements, not just the best one, inside a fixed node budget, and it restarts a
 * handful of times with a lightly shuffled word order. A word with nowhere to go is
 * set aside and retried once the rest are down, because the letter it needs may arrive
 * later. Whatever still has no legal crossing is reported, never forced in.
 */

import { segmentGraphemes } from "@/lib/graphemes";

export type CrosswordDirection = "across" | "down";

export interface CrosswordWord {
  /** Position in the accepted word list. */
  id: number;
  /** The answer as typed, e.g. "ice cream". */
  term: string;
  clue: string;
  /** One grapheme per cell, upper-cased. */
  letters: string[];
  /** Letter counts per word, e.g. "(3,5)" for "ice cream". */
  enumeration: string;
}

export interface CrosswordEntry extends CrosswordWord {
  number: number;
  row: number;
  col: number;
  direction: CrosswordDirection;
}

export interface CrosswordLayout {
  rows: number;
  cols: number;
  /** A letter per cell, or null for an unused square. */
  grid: (string | null)[][];
  /** The clue number printed in a cell, or null. */
  numbers: (number | null)[][];
  across: CrosswordEntry[];
  down: CrosswordEntry[];
  unplaced: CrosswordWord[];
  /** Squares shared by an across and a down answer. */
  intersections: number;
}

export type RejectReason = "letters" | "short" | "long" | "duplicate";

export interface RejectedTerm {
  term: string;
  reason: RejectReason;
}

export const MIN_ANSWER_LENGTH = 2;
export const MAX_GRID_SIZE = 25;

/** Spaces, hyphens, apostrophes and full stops vanish from an answer: ICE CREAM → ICECREAM. */
const IGNORABLE = /[\s\-'’.]/gu;
const LETTERS_ONLY = /^(?:\p{L}\p{M}*)+$/u;
const WHITESPACE = /\s+/u;
const HYPHEN = /-/u;
const NOT_LETTER = /[^\p{L}\p{M}]/gu;

function enumerate(term: string, locale?: string): string {
  const groups = term
    .trim()
    .split(WHITESPACE)
    .map((group) =>
      group
        .split(HYPHEN)
        .map(
          (part) =>
            segmentGraphemes(part.replace(NOT_LETTER, ""), locale).length
        )
        .filter((count) => count > 0)
    )
    .filter((parts) => parts.length > 0);
  return `(${groups.map((parts) => parts.join("-")).join(",")})`;
}

/**
 * Normalize typed terms into answers, and say why any were refused.
 *
 * Letters only (any script), two to `MAX_GRID_SIZE` of them; a repeated answer is kept
 * once, because two clues for one answer cannot both be placed.
 */
export function prepareCrosswordWords(
  entries: readonly { term: string; clue: string }[],
  locale?: string
): { words: CrosswordWord[]; rejected: RejectedTerm[] } {
  const words: CrosswordWord[] = [];
  const rejected: RejectedTerm[] = [];
  const seen = new Set<string>();
  for (const { term, clue } of entries) {
    const answer = term
      .normalize("NFC")
      .toLocaleUpperCase(locale)
      .replace(IGNORABLE, "");
    if (!LETTERS_ONLY.test(answer)) {
      rejected.push({ term, reason: "letters" });
      continue;
    }
    const letters = segmentGraphemes(answer, locale);
    if (letters.length < MIN_ANSWER_LENGTH) {
      rejected.push({ term, reason: "short" });
      continue;
    }
    if (letters.length > MAX_GRID_SIZE) {
      rejected.push({ term, reason: "long" });
      continue;
    }
    if (seen.has(answer)) {
      rejected.push({ term, reason: "duplicate" });
      continue;
    }
    seen.add(answer);
    words.push({
      id: words.length,
      term: term.trim(),
      clue: clue.trim(),
      letters,
      enumeration: enumerate(term, locale),
    });
  }
  return { words, rejected };
}

/* ------------------------------------------------------------------ the board */

interface Cell {
  letter: string;
  row: number;
  col: number;
  across: boolean;
  down: boolean;
}

interface Placement {
  word: number;
  row: number;
  col: number;
  direction: CrosswordDirection;
}

interface Board {
  cells: Map<string, Cell>;
  byLetter: Map<string, Set<Cell>>;
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
  placements: Placement[];
}

interface Undo {
  created: Cell[];
  crossed: Cell[];
  direction: CrosswordDirection;
  bounds: [number, number, number, number];
}

interface Candidate extends Placement {
  crossings: number;
  area: number;
  balance: number;
  tie: number;
}

const key = (row: number, col: number) => `${row},${col}`;

function step(direction: CrosswordDirection): [number, number] {
  return direction === "across" ? [0, 1] : [1, 0];
}

function emptyBoard(): Board {
  return {
    cells: new Map(),
    byLetter: new Map(),
    minRow: 0,
    maxRow: 0,
    minCol: 0,
    maxCol: 0,
    placements: [],
  };
}

function occupied(board: Board, row: number, col: number): boolean {
  return board.cells.has(key(row, col));
}

/**
 * How many letters a placement would share with the board, or −1 if it breaks a rule:
 * a clash of letters, an overlap with a word running the same way, a letter touching
 * another word side-on, or a letter directly before or after the word.
 */
function crossingsFor(
  board: Board,
  letters: readonly string[],
  row: number,
  col: number,
  direction: CrosswordDirection
): number {
  const [dr, dc] = step(direction);
  const length = letters.length;
  if (
    occupied(board, row - dr, col - dc) ||
    occupied(board, row + dr * length, col + dc * length)
  ) {
    return -1;
  }
  let crossings = 0;
  for (let k = 0; k < length; k += 1) {
    const r = row + dr * k;
    const c = col + dc * k;
    const cell = board.cells.get(key(r, c));
    if (cell) {
      if (cell.letter !== letters[k] || cell[direction]) {
        return -1;
      }
      crossings += 1;
    } else if (
      occupied(board, r + dc, c + dr) ||
      occupied(board, r - dc, c - dr)
    ) {
      return -1;
    }
  }
  return crossings;
}

function place(
  board: Board,
  letters: readonly string[],
  placement: Placement
): Undo {
  const undo: Undo = {
    created: [],
    crossed: [],
    direction: placement.direction,
    bounds: [board.minRow, board.maxRow, board.minCol, board.maxCol],
  };
  const [dr, dc] = step(placement.direction);
  for (const [k, letter] of letters.entries()) {
    const row = placement.row + dr * k;
    const col = placement.col + dc * k;
    const existing = board.cells.get(key(row, col));
    if (existing) {
      existing[placement.direction] = true;
      undo.crossed.push(existing);
      continue;
    }
    const cell: Cell = {
      letter,
      row,
      col,
      across: placement.direction === "across",
      down: placement.direction === "down",
    };
    board.cells.set(key(row, col), cell);
    const bucket = board.byLetter.get(letter) ?? new Set<Cell>();
    bucket.add(cell);
    board.byLetter.set(letter, bucket);
    undo.created.push(cell);
  }
  const endRow = placement.row + dr * (letters.length - 1);
  const endCol = placement.col + dc * (letters.length - 1);
  if (board.placements.length === 0) {
    board.minRow = placement.row;
    board.minCol = placement.col;
    board.maxRow = endRow;
    board.maxCol = endCol;
  } else {
    board.minRow = Math.min(board.minRow, placement.row);
    board.minCol = Math.min(board.minCol, placement.col);
    board.maxRow = Math.max(board.maxRow, endRow);
    board.maxCol = Math.max(board.maxCol, endCol);
  }
  board.placements.push(placement);
  return undo;
}

function unplace(board: Board, undo: Undo): void {
  for (const cell of undo.crossed) {
    cell[undo.direction] = false;
  }
  for (const cell of undo.created) {
    board.cells.delete(key(cell.row, cell.col));
    board.byLetter.get(cell.letter)?.delete(cell);
  }
  [board.minRow, board.maxRow, board.minCol, board.maxCol] = undo.bounds;
  board.placements.pop();
}

function byPreference(a: Candidate, b: Candidate): number {
  return (
    b.crossings - a.crossings ||
    a.area - b.area ||
    a.balance - b.balance ||
    a.tie - b.tie
  );
}

const DIRECTIONS = ["across", "down"] as const;

/** Where a word starts if its `index`-th letter sits on `cell`. */
function startFor(
  cell: Cell,
  index: number,
  direction: CrosswordDirection
): [number, number] {
  return direction === "across"
    ? [cell.row, cell.col - index]
    : [cell.row - index, cell.col];
}

/** A legal crossing placement, scored — or null. */
function evaluate(
  board: Board,
  word: CrosswordWord,
  start: [number, number],
  direction: CrosswordDirection,
  maxSize: number,
  random: () => number
): Candidate | null {
  const [row, col] = start;
  const crossings = crossingsFor(board, word.letters, row, col, direction);
  if (crossings < 1) {
    return null;
  }
  const [dr, dc] = step(direction);
  const endRow = row + dr * (word.letters.length - 1);
  const endCol = col + dc * (word.letters.length - 1);
  const height =
    Math.max(board.maxRow, endRow) - Math.min(board.minRow, row) + 1;
  const width =
    Math.max(board.maxCol, endCol) - Math.min(board.minCol, col) + 1;
  if (height > maxSize || width > maxSize) {
    return null;
  }
  return {
    word: word.id,
    row,
    col,
    direction,
    crossings,
    area: height * width,
    balance: Math.abs(height - width),
    tie: random(),
  };
}

/** Every legal placement of a word that crosses the board, best first. */
function candidatesFor(
  board: Board,
  word: CrosswordWord,
  maxSize: number,
  random: () => number
): Candidate[] {
  const seen = new Set<string>();
  const found: Candidate[] = [];
  for (const [index, letter] of word.letters.entries()) {
    for (const cell of board.byLetter.get(letter) ?? []) {
      for (const direction of DIRECTIONS) {
        const start = startFor(cell, index, direction);
        const id = `${start[0]},${start[1]},${direction}`;
        if (cell[direction] || seen.has(id)) {
          continue;
        }
        seen.add(id);
        const candidate = evaluate(
          board,
          word,
          start,
          direction,
          maxSize,
          random
        );
        if (candidate) {
          found.push(candidate);
        }
      }
    }
  }
  return found.sort(byPreference);
}

/* ------------------------------------------------------------------ the search */

interface Snapshot {
  placements: Placement[];
  intersections: number;
  area: number;
  balance: number;
}

function snapshot(board: Board): Snapshot {
  let intersections = 0;
  for (const cell of board.cells.values()) {
    if (cell.across && cell.down) {
      intersections += 1;
    }
  }
  const height = board.maxRow - board.minRow + 1;
  const width = board.maxCol - board.minCol + 1;
  return {
    placements: board.placements.slice(),
    intersections,
    area: height * width,
    balance: Math.abs(height - width),
  };
}

function isBetter(a: Snapshot, b: Snapshot | null): boolean {
  if (b === null) {
    return true;
  }
  return (
    (a.placements.length - b.placements.length ||
      a.intersections - b.intersections ||
      b.area - a.area ||
      b.balance - a.balance) > 0
  );
}

interface SearchOptions {
  random: () => number;
  maxSize: number;
  nodeBudget: number;
  branching: number;
}

/** Retry set-aside words now that more letters are down, until nothing else fits. */
function placeDeferred(
  board: Board,
  words: readonly CrosswordWord[],
  deferred: readonly number[],
  options: SearchOptions
): Undo[] {
  const undos: Undo[] = [];
  let pending = deferred.slice();
  let progress = true;
  while (progress && pending.length > 0) {
    progress = false;
    const still: number[] = [];
    for (const id of pending) {
      const word = words[id] as CrosswordWord;
      const [best] = candidatesFor(
        board,
        word,
        options.maxSize,
        options.random
      );
      if (best) {
        undos.push(place(board, word.letters, best));
        progress = true;
      } else {
        still.push(id);
      }
    }
    pending = still;
  }
  return undos;
}

interface SearchState {
  board: Board;
  words: readonly CrosswordWord[];
  /** The word order after the first, fixed, longest word. */
  rest: readonly number[];
  options: SearchOptions;
  nodes: number;
  best: Snapshot | null;
}

function visit(state: SearchState, index: number, deferred: number[]): void {
  const { board, words, rest, options } = state;
  state.nodes += 1;
  if (index === rest.length) {
    const undos = placeDeferred(board, words, deferred, options);
    const leaf = snapshot(board);
    if (isBetter(leaf, state.best)) {
      state.best = leaf;
    }
    for (const undo of undos.reverse()) {
      unplace(board, undo);
    }
    return;
  }
  const word = words[rest[index] as number] as CrosswordWord;
  const choices = candidatesFor(
    board,
    word,
    options.maxSize,
    options.random
  ).slice(0, options.branching);
  if (choices.length === 0) {
    visit(state, index + 1, [...deferred, word.id]);
    return;
  }
  for (const [branch, choice] of choices.entries()) {
    // Past the budget, finish the branch in hand but stop opening new ones.
    if (branch > 0 && state.nodes > options.nodeBudget) {
      break;
    }
    const undo = place(board, word.letters, choice);
    visit(state, index + 1, deferred);
    unplace(board, undo);
  }
}

function search(
  words: readonly CrosswordWord[],
  order: readonly number[],
  options: SearchOptions
): Snapshot {
  const board = emptyBoard();
  const first = words[order[0] as number] as CrosswordWord;
  place(board, first.letters, {
    word: first.id,
    row: 0,
    col: 0,
    direction: "across",
  });
  const state: SearchState = {
    board,
    words,
    rest: order.slice(1),
    options,
    nodes: 0,
    best: null,
  };
  visit(state, 0, []);
  return state.best ?? snapshot(board);
}

/** Longest first; equal lengths keep the order they were typed in. */
function lengthOrder(words: readonly CrosswordWord[]): number[] {
  return words
    .map((word) => word.id)
    .sort(
      (a, b) =>
        (words[b] as CrosswordWord).letters.length -
          (words[a] as CrosswordWord).letters.length || a - b
    );
}

/** The longest word stays first; the rest are shuffled among near-equal lengths. */
function jitteredOrder(
  words: readonly CrosswordWord[],
  base: readonly number[],
  random: () => number
): number[] {
  const [first, ...rest] = base;
  const keyed = rest.map((id) => ({
    id,
    weight: (words[id] as CrosswordWord).letters.length + random() * 3,
  }));
  keyed.sort((a, b) => b.weight - a.weight);
  return [first as number, ...keyed.map((item) => item.id)];
}

function numberStarts(
  placements: readonly Placement[],
  minRow: number,
  minCol: number
): Map<string, number> {
  const starts = placements
    .map((p) => ({ row: p.row - minRow, col: p.col - minCol }))
    .sort((a, b) => a.row - b.row || a.col - b.col);
  const numbers = new Map<string, number>();
  for (const start of starts) {
    const id = key(start.row, start.col);
    if (!numbers.has(id)) {
      numbers.set(id, numbers.size + 1);
    }
  }
  return numbers;
}

function buildLayout(
  words: readonly CrosswordWord[],
  best: Snapshot
): CrosswordLayout {
  const board = emptyBoard();
  for (const placement of best.placements) {
    place(board, (words[placement.word] as CrosswordWord).letters, placement);
  }
  const rows = board.maxRow - board.minRow + 1;
  const cols = board.maxCol - board.minCol + 1;
  const grid: (string | null)[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, (): string | null => null)
  );
  const numbers: (number | null)[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, (): number | null => null)
  );
  for (const cell of board.cells.values()) {
    const row = grid[cell.row - board.minRow];
    if (row) {
      row[cell.col - board.minCol] = cell.letter;
    }
  }
  const numbering = numberStarts(best.placements, board.minRow, board.minCol);
  for (const [id, value] of numbering) {
    const [r, c] = id.split(",").map(Number) as [number, number];
    const row = numbers[r];
    if (row) {
      row[c] = value;
    }
  }
  const entries = best.placements.map((placement) => {
    const row = placement.row - board.minRow;
    const col = placement.col - board.minCol;
    return {
      ...(words[placement.word] as CrosswordWord),
      row,
      col,
      direction: placement.direction,
      number: numbering.get(key(row, col)) ?? 0,
    };
  });
  const placed = new Set(best.placements.map((p) => p.word));
  return {
    rows,
    cols,
    grid,
    numbers,
    across: entries
      .filter((entry) => entry.direction === "across")
      .sort((a, b) => a.number - b.number),
    down: entries
      .filter((entry) => entry.direction === "down")
      .sort((a, b) => a.number - b.number),
    unplaced: words.filter((word) => !placed.has(word.id)),
    intersections: best.intersections,
  };
}

export interface LayoutOptions {
  random: () => number;
  /** Fresh starts with a shuffled word order. */
  restarts?: number;
  /** Search nodes per start before it stops branching. */
  nodeBudget?: number;
  /** Placements tried per word. */
  branching?: number;
  maxSize?: number;
}

export function layoutCrossword(
  words: readonly CrosswordWord[],
  {
    random,
    restarts = 8,
    nodeBudget = 300,
    branching = 3,
    maxSize = MAX_GRID_SIZE,
  }: LayoutOptions
): CrosswordLayout {
  if (words.length === 0) {
    return {
      rows: 0,
      cols: 0,
      grid: [],
      numbers: [],
      across: [],
      down: [],
      unplaced: [],
      intersections: 0,
    };
  }
  const options: SearchOptions = { random, maxSize, nodeBudget, branching };
  const base = lengthOrder(words);
  let best: Snapshot | null = null;
  for (let attempt = 0; attempt < Math.max(1, restarts); attempt += 1) {
    const order = attempt === 0 ? base : jitteredOrder(words, base, random);
    const result = search(words, order, options);
    if (isBetter(result, best)) {
      best = result;
    }
  }
  return buildLayout(words, best as Snapshot);
}

type Grid = readonly (readonly (string | null)[])[];

export interface CrosswordRun {
  row: number;
  col: number;
  direction: CrosswordDirection;
  text: string;
}

/** The run of letters that starts at a cell, if one starts there and is ≥ 2 long. */
function runFrom(
  grid: Grid,
  row: number,
  col: number,
  direction: CrosswordDirection
): CrosswordRun | null {
  const [dr, dc] = step(direction);
  const at = (r: number, c: number) => grid[r]?.[c] ?? null;
  if (at(row, col) === null || at(row - dr, col - dc) !== null) {
    return null;
  }
  let text = "";
  let k = 0;
  while (at(row + dr * k, col + dc * k) !== null) {
    text += at(row + dr * k, col + dc * k);
    k += 1;
  }
  return k >= 2 ? { row, col, direction, text } : null;
}

/**
 * Every run of two or more letters in the grid, across and down — what a solver would
 * read as an answer. A legal layout has exactly one run per placed word.
 */
export function crosswordRuns(grid: Grid): CrosswordRun[] {
  const runs: CrosswordRun[] = [];
  for (const [r, cells] of grid.entries()) {
    for (const c of cells.keys()) {
      for (const direction of DIRECTIONS) {
        const run = runFrom(grid, r, c, direction);
        if (run) {
          runs.push(run);
        }
      }
    }
  }
  return runs;
}
